import { BusinessModel } from '@/http/models/business/Business'
import { sidebarConfig, SidebarItem } from '@/config/sidebar'

import { createContext, useContext, useEffect, useState } from 'react'

import { BusinessResponses } from '@/types'

import { useAuth } from './AuthContext'

interface FeatureFlagsContextType {
  featureFlags: BusinessResponses.FeatureFlags | null
  businessProfile: any | null
  isLoading: boolean
  isFeatureEnabled: (path: string) => boolean
  refreshFeatureFlags: () => Promise<void>
  getSidebarItems: () => SidebarItem[]
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType>(null!)

export const FeatureFlagsProvider = ({ children }: { children: React.ReactNode }) => {
  const [featureFlags, setFeatureFlags] = useState<BusinessResponses.FeatureFlags | null>(null)
  const [businessProfile, setBusinessProfile] = useState<any | null>(null) // Using any for now
  const [isLoading, setIsLoading] = useState(true)
  const { user, can } = useAuth()

  const fetchFeatureFlags = async () => {
    try {
      const response = await BusinessModel.getBusinessProfile()
      if (response.success && response.data?.result) {

        const profile = response.data.result
        setBusinessProfile(profile)

        const appSettings = profile.app_settings
        // Top level on the business, set only in System Admin (POS.md O10); the backoffice never writes it.
        const baseFeatureFlags = profile.feature_flags || {}

        let mergedFlags = { ...baseFeatureFlags }


        // Check if user has role_id property (for future role-based permissions)
        const userRoleId = (user as any)?.role_id
        if (userRoleId && appSettings?.role_permissions?.[userRoleId]) {
          const roleFeatures = appSettings.role_permissions[userRoleId].features
          if (roleFeatures) {
            // Merge role-specific features with base features
            // Role-specific settings override base settings
            mergedFlags = mergeFeatureFlags(baseFeatureFlags, roleFeatures)
          }
        }

        setFeatureFlags(mergedFlags)
      }
    } catch (e) {
      console.error('Failed to fetch feature flags', e)
      // Set empty object to allow all features by default on error
      setFeatureFlags({})
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Merge base feature flags with role-specific overrides
   * Role-specific flags take precedence
   */
  const mergeFeatureFlags = (
    base: BusinessResponses.FeatureFlags,
    roleOverrides: BusinessResponses.FeatureFlags
  ): BusinessResponses.FeatureFlags => {
    const merged = { ...base }

    Object.keys(roleOverrides).forEach((key) => {
      const roleValue = roleOverrides[key]

      if (typeof roleValue === 'object' && roleValue !== null && !Array.isArray(roleValue)) {
        merged[key] = {
          ...(merged[key]),
          ...roleValue,
        }
      } else {
        merged[key] = roleValue
      }
    })

    return merged
  }

  useEffect(() => {
    fetchFeatureFlags()
  }, [(user)?.role_ids]) // Re-fetch when user role changes

  /**
   * Check if a feature is enabled based on a dot-notation path
   * Examples:
   * - "media.media_library" -> checks featureFlags.media?.media_library
   * - "customers" -> checks featureFlags.customers
   * - "system.blocklist" -> checks featureFlags.system?.blocklist
   *
   * Returns true if:
   * - Feature flags haven't loaded yet (to prevent UI flashing)
   * - The feature flag is explicitly set to true
   * - The feature flag is undefined (permissive by default)
   *
   * Future: This will also check role-based permissions
   */
  const isFeatureEnabled = (path: string): boolean => {
    if (!featureFlags) return true // Allow all features while loading

    const parts = path.split('.')
    let current = featureFlags

    for (const part of parts) {
      if (current === undefined || current === null) {
        return true // Permissive by default if path doesn't exist
      }
      current = current[part]
    }

    // If the final value is a boolean, use it; otherwise default to true
    return typeof current === 'boolean' ? current : true
  }

  const refreshFeatureFlags = async () => {
    setIsLoading(true)
    await fetchFeatureFlags()
  }

  const getSidebarItems = (): SidebarItem[] => {
    const filterItems = (items: SidebarItem[]): SidebarItem[] => {
      return items.reduce<SidebarItem[]>((acc, item) => {
        // Check if item itself is enabled
        const flags = item.featureFlag === undefined ? [] : ([] as string[]).concat(item.featureFlag)
        if (!flags.every(isFeatureEnabled)) {
          return acc
        }

        if (item.permission && !can(item.permission)) {
          return acc
        }

        // Process children if any
        if (item.children) {
          const filteredChildren = filterItems(item.children)
          // A group whose pages are all hidden (flags or permissions) is hidden too
          if (item.children.length > 0 && filteredChildren.length === 0) return acc
          return [...acc, { ...item, children: filteredChildren }]
        }

        return [...acc, item]
      }, [])
    }

    return filterItems(sidebarConfig)
  }

  return (
    <FeatureFlagsContext.Provider
      value={{
        featureFlags,
        businessProfile,
        isLoading,
        isFeatureEnabled,
        refreshFeatureFlags,
        getSidebarItems,
      }}
    >
      {children}
    </FeatureFlagsContext.Provider>
  )
}

export const useFeatureFlags = () => useContext(FeatureFlagsContext)
